import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useAction } from 'src/hooks/useAction';
import { useBoundAction } from 'src/hooks/useBoundAction';
import { useParametricSelector } from 'src/hooks/useParametricSelector';
import { ProjectToolbar } from 'src/modules/common/components/ProjectToolbar';
import { AppLayout } from 'src/modules/common/layouts/AppLayout';
import { getLanguage } from 'src/modules/config/selectors/getLanguage';
import { getHvacResults } from 'src/modules/hvac/selectors/getHvacResults';
import { UPDATE_USER_SETTINGS } from 'src/modules/login/actions/UserSettingsActions';
import { getLoggedInUser } from 'src/modules/login/selectors/getLoggedInUser';
import { PROJECT_VIEW_SHARE } from 'src/modules/project-view/actions/ProjectViewActions';
import { getProjectClosed } from 'src/modules/project-view/selectors/getProjectClosed';
import { getProjectDetails } from 'src/modules/project-view/selectors/getProjectDetails';
import { REPORT_ATTACHMENT_DELETE, REPORT_ATTACHMENT_PROCESS } from 'src/modules/report/actions/ReportActions';
import { getReportAttachments } from 'src/modules/report/selectors/getReportAttachments';
import { getReportEmbeddings } from 'src/modules/report/selectors/getReportEmbeddings';
import { getReportFormData } from 'src/modules/report/selectors/getReportFormData';
import { getReportGenerated } from 'src/modules/report/selectors/getReportGenerated';
import { getReportLoading } from 'src/modules/report/selectors/getReportLoading';
import { ReportFormView } from 'src/modules/report/views/ReportFormView';
import { ShareProject } from 'src/modules/sharing-projects/containers/ShareProject';

export const ReportPage = (): React.ReactElement => {
  const intl = useIntl();
  const project = useSelector(getProjectDetails);
  const isProjectClosed = useSelector(getProjectClosed);
  const language = useSelector(getLanguage);
  const document = useParametricSelector(getReportGenerated, project);
  const formData = useParametricSelector(getReportFormData, project);
  const loading = useSelector(getReportLoading);
  const embeddings = useSelector(getReportEmbeddings);
  const attachments = useSelector(getReportAttachments);
  const loggedInUser = useSelector(getLoggedInUser);
  const results = useParametricSelector(getHvacResults, project);

  const updateUserSettings = useAction(UPDATE_USER_SETTINGS.trigger);
  const shareProject = useBoundAction(PROJECT_VIEW_SHARE.trigger, project);

  const deleteAttachment = useAction(REPORT_ATTACHMENT_DELETE.trigger);
  const uploadAttachment = useAction(REPORT_ATTACHMENT_PROCESS.request);

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({ id: 'report/pageTitle' })}
        </title>
      </Helmet>

      <AppLayout>
        <ProjectToolbar
          project={project}
          onShare={shareProject}
        />
        <ReportFormView
          results={results}
          project={project}
          document={document}
          formData={formData}
          loading={loading}
          language={language}
          embeddings={embeddings}
          attachments={attachments}
          userSettings={loggedInUser.settings}
          useOrganizationContacts={loggedInUser.organization.useOrganizationContacts}
          updateUserSettings={updateUserSettings}
          onUploadAttachment={uploadAttachment}
          onDeleteAttachment={deleteAttachment}
          readOnly={project.readOnly || isProjectClosed}
        />
      </AppLayout>

      <ShareProject/>
    </>
  );
};
